import { compose } from "redux";
import { IAction } from "../../../../models/Action";
import { IProfileMgmtUsers, IUserRole, IVisibilityCone } from "./constants";
import { immutableSet } from "../../../../utils/immutable";
import { actionTypes } from "./actions";
import { STATE_IDENTIFIERS } from "./constants";
import emptyState from "./emptyState";
import { IResponseState } from "../../../../components/Modals/ResponseModal/module/constants";
import { IDENTITY_SYSTEM } from "../../../../module/constants";

const {
  USER_ROLES_MGMT,
  LOADER_IDENTIFIER,
  USERS_IDENTIFIER,
  USER_DETAILS,
  USER_ROLES,
  LIST_IDENTIFIER,
  SELECTED_USER,
  SELECTABLE_ROLES,
  ADDED_ROLES,
  DELETED_ROLES,
  RESPONSE_STATE,
  ROLES_MGMT,
  ROLES_IDENTIFIER,
  VISIBILITY_CONES_IDENTIFIER,
  ROLE_USERS_MGMT,
  SELECTED_ROLE,
  USERS_LOADER_IDENTIFIER,
  SELECTED_USERS_LOADER_IDENTIFIER,
  SELECTED_USERS,
  ROLE_TO_EDIT,
  VISIBILITY_CONE_TO_EDIT,
  FAILURES,
  SELECTED_USERS_CODES,
  ROLES_NEW_VISIBILITY_CONE,
} = STATE_IDENTIFIERS;

const ACTION_HANDLERS = {
  [actionTypes.FETCH_ALL_USERS]: (state: any, action: any) =>
    compose(immutableSet([USER_ROLES_MGMT, LOADER_IDENTIFIER], true))(state),
  [actionTypes.FETCH_ALL_USERS_SUCCESS]: (state: any, action: any) => {
    const { users, ...rest } = action.payload;
    const mappedUsrs = users.map((usr: any) => {
      const tempUsr = usr;
      tempUsr.uniqueId = usr.id;
      delete tempUsr.id;
      return {
        ...tempUsr,
        identitySystemId:
          IDENTITY_SYSTEM[usr.identitySystemId as keyof typeof IDENTITY_SYSTEM],
      };
    });
    const mappedResponse = {
      ...rest,
      users: mappedUsrs,
    };
    return compose(
      immutableSet([USER_ROLES_MGMT, LOADER_IDENTIFIER], false),
      immutableSet([USER_ROLES_MGMT, USERS_IDENTIFIER], mappedResponse)
    )(state);
  },
  [actionTypes.FETCH_ALL_USERS_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([USER_ROLES_MGMT, LOADER_IDENTIFIER], false),
      immutableSet([USER_ROLES_MGMT, USERS_IDENTIFIER], [])
    )(state),

  [actionTypes.SELECT_USER_ROLES_MGMT_USER]: (state: any, action: any) =>
    compose(immutableSet([USER_DETAILS, SELECTED_USER], action.payload))(state),

  [actionTypes.FETCH_USER_ROLES]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, USER_ROLES, LOADER_IDENTIFIER], true),
      immutableSet([USER_DETAILS, FAILURES], [])
    )(state),
  [actionTypes.FETCH_USER_ROLES_SUCCESS]: (state: any, action: any) => {
    let rolesToAdd = [] as IUserRole[];
    action.payload.userRoles.forEach((fullRole: IUserRole) => {
      rolesToAdd.push({
        idRole: fullRole.idRole,
        role: fullRole.role,
      });
    });
    return compose(
      immutableSet([USER_DETAILS, USER_ROLES, LOADER_IDENTIFIER], false),
      immutableSet([USER_DETAILS, USER_ROLES, LIST_IDENTIFIER], rolesToAdd),
      immutableSet([USER_DETAILS, ADDED_ROLES], []),
      immutableSet([USER_DETAILS, DELETED_ROLES], [])
    )(state);
  },
  [actionTypes.FETCH_USER_ROLES_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, USER_ROLES, LOADER_IDENTIFIER], false),
      immutableSet([USER_DETAILS, USER_ROLES, LIST_IDENTIFIER], []),
      immutableSet([USER_DETAILS, ADDED_ROLES], []),
      immutableSet([USER_DETAILS, DELETED_ROLES], [])
    )(state),
  [actionTypes.UPDATE_ASSIGNED_ROLES]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, USER_ROLES, LIST_IDENTIFIER], action.payload)
    )(state),

  [actionTypes.FETCH_SELECTABLE_ROLES]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, SELECTABLE_ROLES, LOADER_IDENTIFIER], true),
      immutableSet([USER_DETAILS, FAILURES], [])
    )(state),
  [actionTypes.FETCH_SELECTABLE_ROLES_SUCCESS]: (state: any, action: any) => {
    let rolesToAdd = [] as IUserRole[];
    action.payload.roles.forEach((fullRole: IUserRole) => {
      rolesToAdd.push({
        idRole: fullRole.id,
        role: fullRole.role,
      });
    });
    return compose(
      immutableSet([USER_DETAILS, SELECTABLE_ROLES, LOADER_IDENTIFIER], false),
      immutableSet(
        [USER_DETAILS, SELECTABLE_ROLES, LIST_IDENTIFIER],
        rolesToAdd
      )
    )(state);
  },
  [actionTypes.FETCH_SELECTABLE_ROLES_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, SELECTABLE_ROLES, LOADER_IDENTIFIER], false),
      immutableSet([USER_DETAILS, SELECTABLE_ROLES, LIST_IDENTIFIER], [])
    )(state),

  [actionTypes.SET_ADDED_ROLES]: (state: any, action: any) =>
    compose(immutableSet([USER_DETAILS, ADDED_ROLES], action.payload))(state),
  [actionTypes.SET_DELETED_ROLES]: (state: any, action: any) =>
    compose(immutableSet([USER_DETAILS, DELETED_ROLES], action.payload))(state),

  [actionTypes.SEND_MODIFIED_ROLES]: (state: any, action: any) =>
    compose(
      immutableSet([USER_DETAILS, RESPONSE_STATE], IResponseState.PENDING)
    )(state),
  [actionTypes.SEND_MODIFIED_ROLES_SUCCESS]: (state: any, action: any) => {
    let failures = [] as string[];
    let responseState = IResponseState.SUCCESS;
    if (action.payload.insertInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.insertInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    if (action.payload.deleteInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.deleteInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    return compose(
      immutableSet([USER_DETAILS, RESPONSE_STATE], responseState),
      immutableSet([USER_DETAILS, FAILURES], failures)
    )(state);
  },
  [actionTypes.SEND_MODIFIED_ROLES_FAILURE]: (state: any, action: any) =>
    compose(immutableSet([USER_DETAILS, RESPONSE_STATE], IResponseState.ERROR))(
      state
    ),

  [actionTypes.FETCH_LOAD_ROLES_NEW_VISIBILITY_CONE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [ROLES_MGMT, ROLES_NEW_VISIBILITY_CONE, LOADER_IDENTIFIER],
        true
      ),
      immutableSet([ROLES_MGMT, FAILURES], [])
    )(state),
  [actionTypes.FETCH_LOAD_ROLES_NEW_VISIBILITY_CONE_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let rolesToAdd = [] as any[];
    action.payload.roles.forEach((role: any) => {
      rolesToAdd.push({
        role,
      });
    });
    return compose(
      immutableSet(
        [ROLES_MGMT, ROLES_NEW_VISIBILITY_CONE, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [ROLES_MGMT, ROLES_NEW_VISIBILITY_CONE, LIST_IDENTIFIER],
        rolesToAdd
      )
    )(state);
  },
  [actionTypes.FETCH_LOAD_ROLES_NEW_VISIBILITY_CONE_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [ROLES_MGMT, ROLES_NEW_VISIBILITY_CONE, LOADER_IDENTIFIER],
        false
      ),
      immutableSet([ROLES_MGMT, ROLES_NEW_VISIBILITY_CONE, LIST_IDENTIFIER], [])
    )(state),

  [actionTypes.LOAD_ROLES_MANAGEMENT_ROLES]: (state: any, action: any) =>
    compose(
      immutableSet([ROLES_MGMT, ROLES_IDENTIFIER, LOADER_IDENTIFIER], true),
      immutableSet([ROLES_MGMT, FAILURES], [])
    )(state),
  [actionTypes.LOAD_ROLES_MANAGEMENT_ROLES_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let rolesToAdd = [] as IUserRole[];
    action.payload.roles.forEach((fullRole: IUserRole) => {
      rolesToAdd.push({
        idRole: fullRole.id,
        role: fullRole.role,
      });
    });
    return compose(
      immutableSet([ROLES_MGMT, ROLES_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([ROLES_MGMT, ROLES_IDENTIFIER, LIST_IDENTIFIER], rolesToAdd)
    )(state);
  },
  [actionTypes.LOAD_ROLES_MANAGEMENT_ROLES_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([ROLES_MGMT, ROLES_IDENTIFIER, LOADER_IDENTIFIER], false),
      immutableSet([ROLES_MGMT, ROLES_IDENTIFIER, LIST_IDENTIFIER], [])
    )(state),

  [actionTypes.LOAD_ROLES_MANAGEMENT_VISIBILITY_CONES]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [ROLES_MGMT, VISIBILITY_CONES_IDENTIFIER, LOADER_IDENTIFIER],
        true
      ),
      immutableSet([ROLES_MGMT, FAILURES], [])
    )(state),
  [actionTypes.LOAD_ROLES_MANAGEMENT_VISIBILITY_CONES_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let visibilityConesToAdd = [] as IVisibilityCone[];
    action.payload.visibility.forEach((fullCone: IVisibilityCone) => {
      visibilityConesToAdd.push({
        id: fullCone.id,
        role: fullCone.role,
        object: fullCone.object,
        consoleAdminProfilesManagementVisibility: fullCone.visibility,
      });
    });
    return compose(
      immutableSet(
        [ROLES_MGMT, VISIBILITY_CONES_IDENTIFIER, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [ROLES_MGMT, VISIBILITY_CONES_IDENTIFIER, LIST_IDENTIFIER],
        visibilityConesToAdd
      )
    )(state);
  },
  [actionTypes.LOAD_ROLES_MANAGEMENT_VISIBILITY_CONES_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet(
        [ROLES_MGMT, VISIBILITY_CONES_IDENTIFIER, LOADER_IDENTIFIER],
        false
      ),
      immutableSet(
        [ROLES_MGMT, VISIBILITY_CONES_IDENTIFIER, LIST_IDENTIFIER],
        []
      )
    )(state),

  [actionTypes.SET_ROLE_USERS_SELECTED_ROLE]: (state: any, action: any) =>
    compose(immutableSet([ROLE_USERS_MGMT, SELECTED_ROLE], action.payload))(
      state
    ),

  [actionTypes.CREATE_OR_UPDATE_ROLE_ACTION]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.PENDING))(
      state
    ),
  [actionTypes.CREATE_OR_UPDATE_ROLE_ACTION_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let failures = [] as string[];
    let responseState = IResponseState.SUCCESS;
    if (action.payload.insertOrUpdateInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.insertOrUpdateInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    return compose(
      immutableSet([ROLES_MGMT, RESPONSE_STATE], responseState),
      immutableSet([ROLES_MGMT, FAILURES], failures)
    )(state);
  },
  [actionTypes.CREATE_OR_UPDATE_ROLE_ACTION_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.ERROR))(
      state
    ),

  [actionTypes.DELETE_ROLE_ACTION]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.PENDING))(
      state
    ),
  [actionTypes.DELETE_ROLE_ACTION_SUCCESS]: (state: any, action: any) => {
    let failures = [] as string[];
    let responseState = IResponseState.SUCCESS;
    if (action.payload.deleteInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.deleteInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    return compose(
      immutableSet([ROLES_MGMT, RESPONSE_STATE], responseState),
      immutableSet([ROLES_MGMT, FAILURES], failures)
    )(state);
  },

  [actionTypes.DELETE_ROLE_ACTION_FAILURE]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.ERROR))(
      state
    ),

  [actionTypes.CREATE_OR_UPDATE_VISIBILITY_CONE]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.PENDING))(
      state
    ),
  [actionTypes.CREATE_OR_UPDATE_VISIBILITY_CONE_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let failures = [] as string[];
    let responseState = IResponseState.SUCCESS;
    if (action.payload.insertOrUpdateInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.insertOrUpdateInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    return compose(
      immutableSet([ROLES_MGMT, RESPONSE_STATE], responseState),
      immutableSet([ROLES_MGMT, FAILURES], failures)
    )(state);
  },
  [actionTypes.CREATE_OR_UPDATE_VISIBILITY_CONE_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.ERROR))(
      state
    ),

  [actionTypes.DELETE_VISIBILITY_CONE]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.PENDING))(
      state
    ),
  [actionTypes.DELETE_VISIBILITY_CONE_SUCCESS]: (state: any, action: any) => {
    let failures = [] as string[];
    let responseState = IResponseState.SUCCESS;
    if (action.payload.deleteInError.length > 0) {
      responseState = IResponseState.ERROR;
      action.payload.deleteInError.forEach((failure: any) => {
        failures.push(failure.role);
      });
    }
    return compose(
      immutableSet([ROLES_MGMT, RESPONSE_STATE], responseState),
      immutableSet([ROLES_MGMT, FAILURES], failures)
    )(state);
  },
  [actionTypes.DELETE_VISIBILITY_CONE_FAILURE]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, RESPONSE_STATE], IResponseState.ERROR))(
      state
    ),

  [actionTypes.SET_ROLE_TO_MODIFY]: (state: any, action: any) =>
    compose(immutableSet([ROLES_MGMT, ROLE_TO_EDIT], action.payload))(state),
  [actionTypes.SET_VISIBILITY_CONE_TO_MODIFY]: (state: any, action: any) =>
    compose(
      immutableSet([ROLES_MGMT, VISIBILITY_CONE_TO_EDIT], action.payload)
    )(state),

  [actionTypes.GET_ROLE_USERS_USERS_ACTION]: (state: any, action: any) =>
    compose(immutableSet([ROLE_USERS_MGMT, USERS_LOADER_IDENTIFIER], true))(
      state
    ),
  [actionTypes.GET_ROLE_USERS_USERS_ACTION_SUCCESS]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, USERS_LOADER_IDENTIFIER], false),
      immutableSet([ROLE_USERS_MGMT, USERS_IDENTIFIER], action.payload)
    )(state),
  [actionTypes.GET_ROLE_USERS_USERS_ACTION_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, USERS_LOADER_IDENTIFIER], false),
      immutableSet([ROLE_USERS_MGMT, USERS_IDENTIFIER], [])
    )(state),

  [actionTypes.GET_ROLE_USERS_SELECTED_USERS_ACTION]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, SELECTED_USERS_LOADER_IDENTIFIER], true)
    )(state),
  [actionTypes.GET_ROLE_USERS_SELECTED_USERS_ACTION_SUCCESS]: (
    state: any,
    action: any
  ) => {
    let roleUsers = action.payload.roles[0].users;
    let usersToAdd = [] as IProfileMgmtUsers[];
    roleUsers.forEach((user: IProfileMgmtUsers) => {
      let userToAdd = {
        name: user.name,
        surname: user.surname,
        code: user.code,
        ou: user.organizational_unit,
        added: false,
        deleted: false,
        identitySystemId:
          IDENTITY_SYSTEM[
            user.identitySystemId as keyof typeof IDENTITY_SYSTEM
          ],
      };
      usersToAdd.push(userToAdd);
    });
    return compose(
      immutableSet([ROLE_USERS_MGMT, SELECTED_USERS_LOADER_IDENTIFIER], false),
      immutableSet([ROLE_USERS_MGMT, SELECTED_USERS], usersToAdd)
    )(state);
  },
  [actionTypes.GET_ROLE_USERS_SELECTED_USERS_ACTION_FAILURE]: (
    state: any,
    action: any
  ) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, SELECTED_USERS_LOADER_IDENTIFIER], false),
      immutableSet([ROLE_USERS_MGMT, SELECTED_USERS], [])
    )(state),

  [actionTypes.UPDATE_ROLE_USERS_SELECTED_USERS_ACTION]: (
    state: any,
    action: any
  ) =>
    compose(immutableSet([ROLE_USERS_MGMT, SELECTED_USERS], action.payload))(
      state
    ),

  [actionTypes.UPDATE_ROLE_USERS_ACTION]: (state: any, action: any) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, RESPONSE_STATE], IResponseState.PENDING)
    )(state),
  [actionTypes.UPDATE_ROLE_USERS_ACTION_SUCCESS]: (state: any, action: any) => {
    let responseState = IResponseState.SUCCESS;
    if (action.payload.insertOrUpdateInError.length > 0) {
      responseState = IResponseState.ERROR;
    }
    return compose(
      immutableSet([ROLE_USERS_MGMT, RESPONSE_STATE], responseState)
    )(state);
  },
  [actionTypes.DELETE_VISIBILITY_CONE_FAILURE]: (state: any, action: any) =>
    compose(
      immutableSet([ROLE_USERS_MGMT, RESPONSE_STATE], IResponseState.ERROR)
    )(state),
};

export default function communicationReducer(
  state = emptyState,
  action: IAction
) {
  if (
    action &&
    action.type === "@@router/LOCATION_CHANGE" &&
    (action.payload.location.pathname === "/welcome" ||
      action.payload.location.pathname === "/admin" ||
      action.payload.location.pathname === "/admin-profiles-management")
  ) {
    return emptyState;
  }
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
